import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock } from 'lucide-react';
import api from '../utils/api'; // External API that now accepts our admin JWT
import { setAdminToken, setAdminUser, setAdminRefreshToken, isAuthenticated } from '../utils/auth';

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('🔐 Login attempt:', { email: formData.email });

    try {
      // Use LOCAL admin backend exclusively
      console.log('🔐 Authenticating with local admin backend...');
      
      const localAdminUrl = window.location.origin;
      const response = await fetch(`${localAdminUrl}/api/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        // Handle error response
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          console.warn('Failed to parse error response as JSON:', jsonError);
          errorData = {};
        }
        throw new Error(errorData.detail || `Authentication failed (${response.status})`);
      }
      
      const data = await response.json();
      
      console.log('✅ Local authentication successful:', {
        user: data.user.email,
        is_admin: data.user.is_admin,
        has_token: !!data.access_token
      });
      
      // All admin users in local backend are admin by design
      console.log('✅ Setting local admin authentication data');
      setAdminToken(data.access_token);
      setAdminUser(data.user);
      
      // Store refresh token if provided
      if (data.refresh_token) {
        setAdminRefreshToken(data.refresh_token);
        console.log('✅ Refresh token stored for automatic token refresh');
      }
      
      console.log('✅ Navigating to dashboard...');
      navigate('/dashboard');
      
    } catch (err) {
      console.error('❌ Local authentication error:', err);
      
      // Better error message handling
      let errorMessage = 'Authentication failed. Please check your credentials and try again.';
      
      if (err.message) {
        if (err.message.includes('401') || err.message.includes('Invalid') || err.message.includes('Unauthorized')) {
          errorMessage = 'Incorrect Login ID/Password. Please try again.';
        } else if (err.message.includes('404') || err.message.includes('Not Found')) {
          errorMessage = 'Authentication service not available. Please contact support.';
        } else if (err.message.includes('ECONNREFUSED') || err.message.includes('ERR_NETWORK')) {
          errorMessage = 'Unable to connect to authentication service.';
        } else if (err.message.includes('body is disturbed')) {
          errorMessage = 'Incorrect Login ID/Password. Please try again.';
        } else if (err.message !== 'Authentication failed. Please check your credentials and try again.') {
          // If there's a specific backend error message, use it only if it's user-friendly
          const message = err.message.toLowerCase();
          if (message.includes('credential') || message.includes('password') || message.includes('email') || message.includes('invalid')) {
            errorMessage = 'Incorrect Login ID/Password. Please try again.';
          }
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-16 h-16 bg-black rounded-full flex items-center justify-center mb-2">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">Admin Portal</CardTitle>
          <CardDescription className="text-gray-600">
            The Haute Circle - Administrative Access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="h-11"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            <Button 
              type="submit" 
              className="w-full h-11 bg-black hover:bg-gray-800 text-white font-medium transition-colors" 
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login to Dashboard'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;